import {ProductReview} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_reviews.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  FONT_SIZES,
  StarRating,
  Text,
  desktopAndUp,
} from '@aboutyou/ui-components';
import React, {FC, useState} from 'react';
import styled, {css} from 'styled-components';
import {starRatingMap} from './OverallRating';

export const UserReviewCard: FC<{
  showcaseReview: ProductReview;
  maxReviewTextCharacters?: number;
  reducePadding?: boolean;
  reducedWith?: boolean;
  reduceMargin?: boolean;
  className?: string;
}> = ({
  showcaseReview,
  maxReviewTextCharacters = 120,
  reducedWith = false,
  reducePadding = false,
  reduceMargin = false,
  className,
}) => {
  return (
    <Wrapper
      reducedWith={reducedWith}
      reducePadding={reducePadding}
      reduceMargin={reduceMargin}
      data-testid="userReview"
      className={className}
    >
      <UserReviewStarsAndUser>
        <StarRating
          value={starRatingMap(showcaseReview.stars)}
          size="L"
          dataTestid="userReviewStarRating"
        />
        <Text color="Grey1" data-testid="userReviewAuthorLabel">
          {showcaseReview.authorLabel}
        </Text>
      </UserReviewStarsAndUser>
      <ReviewTitle weight="bold" data-testid="userReviewTitle">
        {showcaseReview.title}
      </ReviewTitle>
      <UserTextReviews
        maxReviewTextCharacters={maxReviewTextCharacters}
        reviewText={showcaseReview.text}
        dataTestId="userReviewText"
      />
    </Wrapper>
  );
};

const UserTextReviews: FC<{
  reviewText: string;
  dataTestId: string;
  maxReviewTextCharacters: number;
}> = ({reviewText, dataTestId, maxReviewTextCharacters}) => {
  const [shortenText, setShortenText] = useState(true);
  const {t} = useTranslation();
  const isLongText = reviewText.length > maxReviewTextCharacters;

  return (
    <ReviewText data-testid={dataTestId}>
      {isLongText && shortenText
        ? `${reviewText.substring(0, maxReviewTextCharacters)}...`
        : reviewText}
      {isLongText && (
        <SeeMoreLessButton
          showInNextLine={!shortenText}
          onClick={() => setShortenText(!shortenText)}
        >
          {' '}
          {shortenText ? t('Read more') : t('Read less')}
        </SeeMoreLessButton>
      )}
    </ReviewText>
  );
};

const Wrapper = styled.div<{
  reducePadding: boolean;
  reducedWith: boolean;
  reduceMargin: boolean;
}>`
  border: 1px solid ${COLORS.Grey3};
  border-radius: 2px;
  box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.02), 0 0 15px 0 rgba(0, 0, 0, 0.04);
  padding: 16px 16px 42px 16px;
  ${({reduceMargin}) =>
    reduceMargin &&
    css`
      margin-left: -16px;
      margin-right: 26px;
    `}
  ${({reducePadding}) =>
    reducePadding &&
    css`
      padding: 16px;
    `}
  ${({reducedWith}) =>
    reducedWith &&
    css`
      max-width: 85%;
    `}
`;

const UserReviewStarsAndUser = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: space-between;
`;

const ReviewTitle = styled(Text)`
  display: block;
  margin-bottom: 8px;
  margin-top: 16px;
  text-transform: capitalize;
`;

const ReviewText = styled(Text)`
  display: block;
  line-height: ${FONT_SIZES.px20};
`;

const SeeMoreLessButton = styled.span<{showInNextLine: boolean}>`
  color: ${COLORS.Grey1};
  cursor: pointer;
  ${({showInNextLine}) =>
    showInNextLine &&
    css`
      display: block;
    `}
  width: fit-content;

  ${desktopAndUp(
    css`
      border-bottom: 1px solid transparent;
      &:hover {
        border-bottom-color: ${COLORS.Black};
      }
    `,
  )}
`;
